import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { notifySyncApi, notifyAckApi, type notifySyncResponse } from '@/api/notify'
import type {
  ModalItem,
  notifyModalPayload,
  notifySyncResponseFeedsOverlayItems,
} from '@/api/notify/types'
import { NotifyModalKind } from '@/enums/chat'
import { tradeC2cMyOrderUnreadApi } from '@/api/trade'
import { useSessionStore } from './session'

/**
 * 通知 Store
 * 管理系统通知、订单通知等
 *
 * @example 基本使用
 * ```ts
 * import { useNotifyStore } from '@/stores/notify'
 *
 * const notifyStore = useNotifyStore()
 *
 * // 同步通知数据
 * await notifyStore.syncNotifications()
 *
 * // 获取订单通知队列
 * const notifications = notifyStore.orderNotifyQueue
 *
 * // 获取未读数
 * const unreadCount = notifyStore.centerUnread
 *
 * // 标记通知已读
 * await notifyStore.acknowledgeNotification([msgId])
 *
 * // 移除通知
 * notifyStore.removeOrderNotification(msgId)
 * ```
 */
export const useNotifyStore = defineStore('notify', () => {
  // 通知数据
  const notifyData = ref<notifySyncResponse | null>(null)

  // 订单通知队列（overlay类型）
  const orderNotifyQueue = ref<notifySyncResponseFeedsOverlayItems[]>([])

  // 大转盘抽奖机会弹窗队列
  const luckyWheelModalQueue = ref<notifyModalPayload[]>([])

  const currentLuckyWheelModal = computed(() => luckyWheelModalQueue.value[0] ?? null)

  // 通知中心未读数
  const centerUnread = ref(0)

  // 订单tab未读数
  const tabOrderUnread = ref(0)

  // 加载状态
  const loading = ref(false)

  const updateOrderUnread = (unread: number) => {
    tabOrderUnread.value = unread
  }
  /**
   * 同步通知数据
   */
  async function syncNotifications() {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId) return

    try {
      loading.value = true
      const res = await notifySyncApi()

      if (res.data) {
        notifyData.value = res.data

        // 更新未读数
        centerUnread.value = res.data.center?.unread || 0
        updateOrderUnread(res.data.tab_order?.unread || 0)

        // 添加 overlay 通知到队列
        if (res.data.feeds?.overlay?.items && res.data.feeds.overlay.items.length > 0) {
          res.data.feeds.overlay.items.forEach((item) => {
            addOrderNotification(item)
          })
        }

        // 添加弹窗通知到队列（如大转盘抽奖机会）
        if (res.data.feeds?.modals?.items && res.data.feeds.modals.items.length > 0) {
          res.data.feeds.modals.items.forEach((item) => {
            if (item.kind !== NotifyModalKind.LuckyWheels) return
            enqueueLuckyWheelModal(item)
          })
        }
      }

      return res.data
    } catch (error) {
      console.error('同步通知失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  function getOrderNotificationKey(notification: notifySyncResponseFeedsOverlayItems) {
    if (notification.msg_id)
      return `${notification.kind}:${notification.feed.order_no}:${notification.msg_id}`
    return `${notification.kind}:${notification.feed.order_no}:${notification.sent_at}`
  }

  /**
   * 添加订单通知到队列
   */
  function addOrderNotification(notification: notifySyncResponseFeedsOverlayItems) {
    const key = getOrderNotificationKey(notification)
    const exists = orderNotifyQueue.value.some((item) => getOrderNotificationKey(item) === key)
    if (!exists) {
      orderNotifyQueue.value.push(notification)
    }
  }

  /**
   * 更新首页中间通知
   */
  function updateMiddleNotification(notification: any) {
    if (!notifyData.value) {
      notifyData.value = {
        center: { unread: 0 },
        tab_order: { unread: 0 },
        feeds: {
          middle: { items: [], more: false },
          overlay: { items: [], more: false },
        },
      }
    }

    if (!notifyData.value.feeds) {
      notifyData.value.feeds = {
        middle: { items: [], more: false },
        overlay: { items: [], more: false },
      }
    }

    if (!notifyData.value.feeds.middle) {
      notifyData.value.feeds.middle = { items: [], more: false }
    }

    // 更新或添加中间通知
    notifyData.value.feeds.middle.items = [notification]
  }

  /**
   * 添加大转盘抽奖机会弹窗
   */
  function enqueueLuckyWheelModal(payload: notifyModalPayload) {
    const exists = luckyWheelModalQueue.value.some((item) => item.msg_id === payload.msg_id)
    if (!exists) {
      luckyWheelModalQueue.value.push(payload)
    }
  }

  /**
   * 关闭当前大转盘弹窗
   */
  function dismissLuckyWheelModal() {
    luckyWheelModalQueue.value.shift()
  }

  /**
   * 清空大转盘弹窗队列
   */
  function clearLuckyWheelModals() {
    luckyWheelModalQueue.value = []
  }

  /**
   * 移除订单通知
   */
  function removeOrderNotification(target: number | notifySyncResponseFeedsOverlayItems) {
    const index =
      typeof target === 'number'
        ? orderNotifyQueue.value.findIndex((item) => item.msg_id === target)
        : orderNotifyQueue.value.findIndex(
            (item) => getOrderNotificationKey(item) === getOrderNotificationKey(target),
          )
    if (index !== -1) {
      orderNotifyQueue.value.splice(index, 1)
    }
  }

  /**
   * 标记通知已读
   * @param msgIds 通知消息ID数组
   * @param kind 通知类型，默认为1（悬浮框/首页中间通知已读）
   */
  async function acknowledgeNotification(msgIds: number[], kind: number = 1) {
    try {
      // 逐个标记通知已读
      for (const msgId of msgIds) {
        await notifyAckApi({ kind, msg_id: msgId })
      }

      // 从队列中移除已读通知
      msgIds.forEach((msgId) => {
        removeOrderNotification(msgId)
      })
    } catch (error) {
      console.error('标记通知已读失败:', error)
      throw error
    }
  }

  /**
   * 清空订单通知队列
   */
  function clearOrderNotifications() {
    orderNotifyQueue.value = []
  }

  /**
   * 同步订单未读数（调用接口并更新 tabOrderUnread）
   */
  async function syncOrderUnread() {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId) return

    try {
      const res = await tradeC2cMyOrderUnreadApi()
      if (res.data) {
        updateOrderUnread((res.data.buy?.unread ?? 0) + (res.data.sell?.unread ?? 0))
      }
    } catch (error) {
      console.error('同步订单未读数失败:', error)
    }
  }

  /**
   * 重置 store
   */
  function $reset() {
    notifyData.value = null
    orderNotifyQueue.value = []
    luckyWheelModalQueue.value = []
    centerUnread.value = 0
    tabOrderUnread.value = 0
    loading.value = false
  }

  return {
    // 状态
    notifyData,
    orderNotifyQueue,
    luckyWheelModalQueue,
    currentLuckyWheelModal,
    centerUnread,
    tabOrderUnread,
    loading,

    // 操作
    syncNotifications,
    addOrderNotification,
    updateMiddleNotification,
    enqueueLuckyWheelModal,
    dismissLuckyWheelModal,
    clearLuckyWheelModals,
    getOrderNotificationKey,
    removeOrderNotification,
    acknowledgeNotification,
    clearOrderNotifications,
    syncOrderUnread,
    updateOrderUnread,
    $reset,
  }
})
